import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    storageKey: 'jazzpractice',
    autoRefreshToken: true,
    detectSessionInUrl: false
  }
})

export const signUp = async (email, password, username, fullName) => {
  const { data, error } = await supabase.auth.signUp({ email, password })
  if (error) throw error
  await supabase.from('profiles').insert({ id: data.user.id, username, full_name: fullName })
  return data
}
export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}
export const signOut = () => supabase.auth.signOut()
export const getUser = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null
  const { data: profile } = await supabase.from('profiles').select('*').eq('id', user.id).single()
  return profile
}
export const startSession = async (userId, programId, mode, timerMode) => {
  const { data, error } = await supabase.from('sessions').insert({ user_id: userId, program_id: programId, mode, timer_mode: timerMode, status: 'in_progress' }).select().single()
  if (error) throw error
  return data
}
export const updateSession = async (sessionId, updates) => {
  const { data, error } = await supabase.from('sessions').update(updates).eq('id', sessionId).select().single()
  if (error) throw error
  return data
}
export const completeSession = async (sessionId, totalMinutes, sectionsLog, notes, isPublic) => {
  return updateSession(sessionId, { status: 'completed', completed_at: new Date().toISOString(), total_minutes: totalMinutes, sections_log: sectionsLog, notes, is_public: isPublic })
}
export const getUserSessions = async (userId, limit = 20) => {
  const { data, error } = await supabase.from('sessions').select('*, programs(title, sections)').eq('user_id', userId).eq('status', 'completed').order('completed_at', { ascending: false }).limit(limit)
  if (error) throw error
  return data
}
export const getFeed = async (userId, limit = 20) => {
  const { data: friends } = await supabase.from('friendships').select('requester_id, addressee_id').or(`requester_id.eq.${userId},addressee_id.eq.${userId}`).eq('status', 'accepted')
  const friendIds = (friends || []).map(f => f.requester_id === userId ? f.addressee_id : f.requester_id)
  friendIds.push(userId)
  const { data, error } = await supabase.from('sessions').select('*, profiles(id, username, full_name, avatar_url, instrument, current_streak, privacy_time, privacy_sections, privacy_streak), reactions(id, type, message, user_id, profiles(username))').in('user_id', friendIds).eq('status', 'completed').eq('is_public', true).order('completed_at', { ascending: false }).limit(limit)
  if (error) throw error
  return data
}
export const addReaction = async (sessionId, userId, type, message = null) => {
  const { data, error } = await supabase.from('reactions').upsert({ session_id: sessionId, user_id: userId, type, message }).select().single()
  if (error) throw error
  return data
}
export const removeReaction = async (sessionId, userId, type) => {
  await supabase.from('reactions').delete().eq('session_id', sessionId).eq('user_id', userId).eq('type', type)
}
export const sendNudge = async (fromId, toId, message) => {
  const { data, error } = await supabase.from('nudges').insert({ from_id: fromId, to_id: toId, message }).select().single()
  if (error) throw error
  return data
}
export const getNudges = async (userId) => {
  const { data, error } = await supabase.from('nudges').select('*, profiles!nudges_from_id_fkey(username, full_name, avatar_url)').eq('to_id', userId).eq('is_read', false).order('created_at', { ascending: false })
  if (error) throw error
  return data
}
export const markNudgeRead = async (nudgeId) => {
  await supabase.from('nudges').update({ is_read: true }).eq('id', nudgeId)
}
export const getFriends = async (userId) => {
  const { data, error } = await supabase.from('friendships').select('*, requester:profiles!friendships_requester_id_fkey(id, username, full_name, avatar_url, instrument, current_streak, last_session_date), addressee:profiles!friendships_addressee_id_fkey(id, username, full_name, avatar_url, instrument, current_streak, last_session_date)').or(`requester_id.eq.${userId},addressee_id.eq.${userId}`).eq('status', 'accepted')
  if (error) throw error
  return (data || []).map(f => f.requester_id === userId ? f.addressee : f.requester)
}
export const sendFriendRequest = async (requesterId, addresseeId) => {
  const { data, error } = await supabase.from('friendships').insert({ requester_id: requesterId, addressee_id: addresseeId, status: 'accepted' }).select().single()
  if (error) throw error
  return data
}
export const acceptFriendRequest = async (friendshipId) => {
  await supabase.from('friendships').update({ status: 'accepted' }).eq('id', friendshipId)
}
export const getPrograms = async (instrument = 'all') => {
  const { data, error } = await supabase.from('programs').select('*').or(`instrument.eq.all,instrument.eq.${instrument}`).eq('is_default', true).order('created_at')
  if (error) throw error
  return data
}
export const getWeeklyStats = async (userId) => {
  const weekAgo = new Date()
  weekAgo.setDate(weekAgo.getDate() - 7)
  const { data, error } = await supabase.from('sessions').select('total_minutes, sections_log, completed_at').eq('user_id', userId).eq('status', 'completed').gte('completed_at', weekAgo.toISOString())
  if (error) throw error
  return data
}
export const getMonthlyCalendar = async (userId) => {
  const monthAgo = new Date()
  monthAgo.setDate(monthAgo.getDate() - 30)
  const { data, error } = await supabase.from('sessions').select('completed_at, total_minutes').eq('user_id', userId).eq('status', 'completed').gte('completed_at', monthAgo.toISOString())
  if (error) throw error
  return data
}
export const getAICoachTip = async (section, instrument, level, programTitle) => {
  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ model: 'claude-sonnet-4-20250514', max_tokens: 150, system: `Είσαι AI coach για μουσική μελέτη. Δίνεις σύντομες, πρακτικές οδηγίες σε Ελληνικά για ${instrument} μαθητές επιπέδου ${level}. Μέγιστο 2 προτάσεις. Ενθαρρυντικός τόνος. Χωρίς εισαγωγές.`, messages: [{ role: 'user', content: `Δώσε μια οδηγία για την ενότητα: ${section.name}. Πρόγραμμα: ${programTitle}.` }] }) })
    const data = await response.json()
    return data.content?.[0]?.text || null
  } catch { return null }
}
export const getStudentAssignments = async (studentId) => {
  const { data, error } = await supabase.from('assignments').select('*, profiles!assignments_teacher_id_fkey(username, full_name)').eq('student_id', studentId).order('created_at', { ascending: false })
  if (error) throw error
  return data
}
export const createAssignment = async (teacherId, studentId, title, description, section, dueDate) => {
  const { data, error } = await supabase.from('assignments').insert({ teacher_id: teacherId, student_id: studentId, title, description, section, due_date: dueDate }).select().single()
  if (error) throw error
  return data
}
export const updateProfile = async (userId, updates) => {
  const { data, error } = await supabase.from('profiles').update(updates).eq('id', userId).select().single()
  if (error) throw error
  return data
}
export const searchUsers = async (query) => {
  const { data, error } = await supabase.from('profiles').select('id, username, full_name, avatar_url, instrument, level, current_streak').or(`username.ilike.%${query}%,full_name.ilike.%${query}%`).limit(10)
  if (error) throw error
  return data
}
